import { useState, useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ProductCard from "@/components/ProductCard";
import { useAuth } from "../hooks/use-auth";
import { PRODUCTS } from "../lib/graphql/queries/product";
import { useQuery } from "@apollo/client";
import HeroSection from "../components/Hero";

const HorizontalSlider = ({ children }: { children: React.ReactNode }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative md:hidden">
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 -mx-4 px-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
    </div>
  );
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { data: latestProducts } = useQuery(PRODUCTS, {
    variables: {
      searchValue: "",
      latest: true,
      page: 1,
      limit: 4,
      skinType: null,
      productLine: null,
      productType: null
    }
  });

  const { data: featuredProducts } = useQuery(PRODUCTS, {
    variables: {
      searchValue: "",
      featured: true,
      page: 1,
      limit: 4,
      skinType: null,
      productLine: null,
      productType: null
    }
  });

  const heroSlides = [
    {
      title: "Таны дурлах гоо сайхны бүтээгдэхүүн.",
      subtitle: "",
      description: "Арьс зүйчид Dr. med. Christine Schrammek-Drusio болон Christina Drusio нарын зохион боловсруулсан арьс арчилгааны болон үс, хуйханд зориулагдсан 70 гаруй бүтээгдэхүүнүүд.",
      image: "/cover/cover1.jpg"
    },
    {
      title: "GREEN PEEL® ургамлын гаралтай арьс гуужуулах эмчилгээ.",
      subtitle: "",
      description: "Дэлхийн 70 гаруй улс оронд 65 жилийн турш амжилттай оршин тогтнож, гоо сайхныг түгээж буй үйлчилгээ. Таны арьсны гоо үзэсгэлэнг тодотгох, байгалийн гаралтай эмчилгээ.",
      image: "/cover/cover2.png"
    }
  ];
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const { user, loading } = useAuth();
  const [showVatDialog, setShowVatDialog] = useState(false);

  useEffect(() => {
    if (user) {
      const stored = localStorage.getItem(`vat_config_${user.id}`);
      if (!stored) {
        setShowVatDialog(true);
      }
    }
  }, [user]);

  const handleVatChoice = (hasVat: boolean) => {
    if (user) {
      localStorage.setItem(
        `vat_config_${user.id}`,
        JSON.stringify({ hasVat, userId: user.id })
      );
    }
    setShowVatDialog(false);
  };

  if(!user && !loading) {
    return <Navigate to="/auth" />;
  }

  const featuredItems = featuredProducts?.publicProducts || [];
  const latestItems = latestProducts?.publicProducts || [];

  return (
    <div>
      <HeroSection />

      {/* Welcome Message */}
      <section className="bg-muted/30 py-8 md:py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl md:text-2xl font-light mb-4">Албан ёсны захиалгын системд тавтай морил</h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-3xl mx-auto">
            {`Та хүссэн цагтаа Dr. med. Christine Schrammek Kosmetik брендийн бүтээгдэхүүнүүдийг захиалах боломжтой бөгөөд, бүтээгдэхүүний танилцуулга гарын авлага видэо хичээл гэх мэт олон төрлийн мэдээллүүдийг авах боломжтой.`}
            <br />
            {`Энэхүү систем нь зөвхөн гэрээт харилцагч нарт зориулсан болно.`}
          </p>
        </div>
      </section>

      {/* New Products */}
      <section className="container mx-auto px-4 py-10 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-light mb-2 uppercase">Шинэ бүтээгдэхүүнүүд</h2>
          <div className="w-20 h-1 bg-primary mx-auto" />
        </div>

        {/* Mobile slider */}
        <HorizontalSlider>
          {latestItems.map((product: any) => (
            <div key={product.id} className="flex-shrink-0 w-[160px] snap-start">
              <ProductCard {...product} isNew />
            </div>
          ))}
        </HorizontalSlider>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {latestItems.map((product: any) => (
            <ProductCard key={product.id} {...product} isNew />
          ))}
        </div>
      </section>

      {/* Featured / ОНЦЛОХ */}
      <section className="container mx-auto px-4 py-10 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-light mb-2">ОНЦЛОХ</h2>
          <div className="w-20 h-1 bg-primary mx-auto" />
        </div>

        {/* Mobile slider */}
        <HorizontalSlider>
          {featuredItems.map((product: any) => (
            <div key={product.id} className="flex-shrink-0 w-[160px] snap-start">
              <ProductCard {...product} />
            </div>
          ))}
        </HorizontalSlider>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredItems.map((product: any) => (
            <ProductCard key={product.id} {...product} />
          ))}
        </div>
      </section>

      {/* VAT Preference Dialog */}
      <Dialog open={showVatDialog} onOpenChange={setShowVatDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>НӨАТ-ын мэдээлэл</DialogTitle>
            <DialogDescription>
              Цаашид хийх захиалгууд тань НӨАТ-тай эсэхээ сонгоно уу. Та энэ тохиргоогоо дараа нь Захиалгын хуудаснаас өөрчлөх боломжтой.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button onClick={() => handleVatChoice(true)}>
              Тийм, НӨАТ авна
            </Button>
            <Button
              variant="outline"
              onClick={() => handleVatChoice(false)}
            >
              Үгүй, НӨАТ авахгүй
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
